import type { LockVersion } from '@/services/loanPricingService';

interface LockVersionBannerProps {
  version: LockVersion | null;
  /**
   * Candidate mode: the inspected version is selectable and the local pricing
   * inputs are editable; Set Active applies them. Historical (read-only)
   * inspection leaves this false.
   */
  candidate?: boolean;
  /** One-line below-par reason shown under the candidate copy. */
  belowParLine?: string | null;
}

/**
 * Amber banner that sits above LockStatusCard whenever the workspace is
 * inspecting a lock version. Informational only — the picker rows own the
 * Set Active action.
 */
export function LockVersionBanner({ version, candidate = false, belowParLine = null }: LockVersionBannerProps) {
  const label = version?.label ?? (candidate ? 'Lock version' : 'Historical lock version');
  const captured = version?.capturedAt
    ? new Date(version.capturedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : null;

  return (
    <div className="sn-banner" role="status" aria-live="polite">
      <svg
        className="sn-banner-icon"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <div className="sn-banner-body">
        <div className="sn-banner-eyebrow">
          {candidate ? 'Reviewing lock version' : 'Viewing historical lock version'}
        </div>
        <div className="sn-banner-text">
          <b>{label}</b>
          {captured && <> · captured {captured}</>}
          {candidate
            ? <> — Pricing edits apply when you set it active; concessions save right away.</>
            : <> — editing is disabled. Select the active/current version in the history picker to resume editing.</>}
        </div>
        {candidate && belowParLine && (
          <div className="sn-banner-text sn-banner-line">{belowParLine}</div>
        )}
      </div>
    </div>
  );
}
